import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useDriverAllowanceServiceV1GetDriverAllowanceQuery,
  useDriverAllowanceServiceV1UpdateDriverAllowanceMutation,
  useDriverAllowanceServiceV1GetDriverAllowanceVehicleTypesQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceVehicleModelsQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceTripTypesQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceDistanceTypesQuery,
  useDriverAllowanceServiceV1GetDriverAllowanceWaitingTimeRatesQuery,
  type DriverAllowanceServiceV1UpdateDriverAllowanceBody,
} from "_services/modules/driver_allowance";
import { useVehicleAuxiliaryServiceV1ListVehicleAuxiliariesQuery } from "_services/modules/vehicle-auxiliary";
import { parseFormattedNumber } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

export interface EditDriverAllowanceFormValues {
  vehicleTypeId: string;
  vehicleModelId: string;
  vehicleAttachmentIds: string[];
  distanceTypeId: string;
  tripTypeId: string;
  driverBasicRate: string;
  driverIncentiveRate: string;
  waitingTimeRateId: string;
}

const schema = yup.object({
  vehicleTypeId: yup.string().required("Vehicle Type is required"),
  vehicleModelId: yup.string().required("Vehicle Model is required"),
  vehicleAttachmentIds: yup.array().of(yup.string().required()).default([]),
  distanceTypeId: yup.string().required("Distance Type is required"),
  tripTypeId: yup.string().required("Trip Type is required"),
  driverBasicRate: yup.string().required("Driver Basic Rate is required").test(
    "is-positive-number",
    "Must be a positive number",
    (value) => {
      if (!value) return false;
      const num = parseFormattedNumber(value);
      return !isNaN(num) && num > 0;
    }
  ),
  driverIncentiveRate: yup.string().required("Driver Incentive Rate is required").test(
    "is-non-negative-number",
    "Must be a non-negative number",
    (value) => {
      if (!value && value !== "0") return false;
      const num = parseFormattedNumber(value);
      return !isNaN(num) && num >= 0;
    }
  ),
  waitingTimeRateId: yup.string().required("Waiting Time Rate is required"),
}).required();

const useEditDriverAllowanceForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("driverAllowance");

  const { data: detail } = useDriverAllowanceServiceV1GetDriverAllowanceQuery(
    { id: id || "" },
    { skip: !id }
  );
  const [updateDriverAllowance] = useDriverAllowanceServiceV1UpdateDriverAllowanceMutation();

  const { data: vehicleTypesData } = useDriverAllowanceServiceV1GetDriverAllowanceVehicleTypesQuery();
  const { data: distanceTypesData } = useDriverAllowanceServiceV1GetDriverAllowanceDistanceTypesQuery();
  const { data: waitingTimeRatesData } = useDriverAllowanceServiceV1GetDriverAllowanceWaitingTimeRatesQuery();
  const { data: vehicleAuxiliaryData } = useVehicleAuxiliaryServiceV1ListVehicleAuxiliariesQuery({ status: "active" });

  const vehicleTypes = vehicleTypesData?.vehicle_types ?? [];
  const distanceTypes = distanceTypesData?.distance_types ?? [];
  const waitingTimeRates = waitingTimeRatesData?.waiting_time_rates ?? [];
  const attachmentOptions = vehicleAuxiliaryData?.vehicle_auxiliary?.map((a) => ({
    id: a.id ?? "",
    name: a.auxiliary_name ?? "",
  })) ?? [];

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<EditDriverAllowanceFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      vehicleTypeId: "",
      vehicleModelId: "",
      vehicleAttachmentIds: [],
      distanceTypeId: "",
      tripTypeId: "",
      driverBasicRate: "",
      driverIncentiveRate: "",
      waitingTimeRateId: "",
    },
  });

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditDriverAllowanceFormValues | null>(null);

  const vehicleTypeId = watch("vehicleTypeId");
  const selectedVehicleType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
  const showAttachment = !!(selectedVehicleType as any)?.is_add_on;

  const { data: vehicleModelsData } = useDriverAllowanceServiceV1GetDriverAllowanceVehicleModelsQuery(
    { vehicleTypeId },
    { skip: !vehicleTypeId }
  );
  const { data: tripTypesData } = useDriverAllowanceServiceV1GetDriverAllowanceTripTypesQuery();

  const vehicleModelOptions = vehicleModelsData?.vehicle_models ?? [];
  const tripTypeOptions = tripTypesData?.trip_types ?? [];

  useEffect(() => {
    if (detail) {
      setValue("vehicleTypeId", detail.vehicle_type?.id ?? "");
      setValue("vehicleModelId", detail.vehicle_model?.id ?? "");
      setValue("vehicleAttachmentIds", []);
      setValue("distanceTypeId", detail.distance_type?.id ?? "");
      setValue("tripTypeId", detail.trip_type?.id ?? "");
      setValue("driverBasicRate", String(detail.driver_basic_rate ?? ""));
      setValue("driverIncentiveRate", String(detail.driver_insentif_rate ?? ""));
      setValue("waitingTimeRateId", detail.waiting_time_rate?.id ?? "");
    }
  }, [detail, setValue]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const onSubmit = (data: EditDriverAllowanceFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      const payload: Pick<DriverAllowanceServiceV1UpdateDriverAllowanceBody, 
        'vehicle_type_id' | 'vehicle_model_id' | 'distance_type_id' | 'trip_type_id' | 'driver_basic_rate' | 'driver_insentif_rate' | 'waiting_time_rate_id'> = {
        vehicle_type_id: pendingData.vehicleTypeId,
        vehicle_model_id: pendingData.vehicleModelId,
        distance_type_id: pendingData.distanceTypeId,
        trip_type_id: pendingData.tripTypeId,
        driver_basic_rate: parseFormattedNumber(pendingData.driverBasicRate),
        driver_insentif_rate: parseFormattedNumber(pendingData.driverIncentiveRate),
        waiting_time_rate_id: pendingData.waitingTimeRateId,
      };
      await updateDriverAllowance({ id, driverAllowanceServiceV1UpdateDriverAllowanceBody: payload }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("tripTypeId", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
      }
      setShowSaveModal(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    handleSubmit,
    register,
    errors,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    showAttachment,
    vehicleModelOptions,
    tripTypeOptions,
    setValue,
    setError,
    watch,
    t,
    vehicleTypes,
    distanceTypes,
    waitingTimeRates,
    attachmentOptions,
  };
};

export default useEditDriverAllowanceForm;
